import React, { useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import {
  Box,
  Button,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'

import { ReactComponent as Download } from '../../../assets/icons/download-simple.svg'
import { ReactComponent as Delete } from '../../../assets/icons/trash.svg'
import { ReactComponent as Path } from '../../../assets/icons/path.svg'

import Loader from '../../../ui/views/Loader'

import useTranslations from '../../../i18n/useTranslations'
import EnhancedTableHead from './EnhancedTableHead'
import EnhancedTableToolbar from './EnhancedTableToolbar'
import {
  SHIPPINGS_RECORDING_TABLE_HEADER_ORDER,
  SHIPPINGS_RECORDING_TABLE_HEAD_CELLS,
} from '../../../common/constants'
import StatusChip from '../../../ui/components/chip/StatusChip'
import { useStyles } from '../styles'
import ModalLayout from '../../../ui/modals/layout'
import * as routes from '../../../routes'
import { Link, NavLink } from 'react-router-dom/cjs/react-router-dom'
import { format, parseISO } from 'date-fns'

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1
  }
  if (b[orderBy] > a[orderBy]) {
    return 1
  }
  return 0
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) return order
    return a[1] - b[1]
  })
  return stabilizedThis.map(el => el[0])
}

//TODO: REFACTOR TO AVOID THE NECESSITY OF RECEIVING PAGING PARAMS
//THIS SHOULD BE OPTIONAL
const DEFAULT_PAGING_PARAMS = {
  page: 0,
  rowsPerPage: 10,
  totalRows: 28,
}

const ShippingsRecordingTable = ({
  onChangeTableParams,
  defaultSortType = 'desc',
  rows,
  handleSubmitDelete,
  handleSubmitGetDetail,
  tableParams = DEFAULT_PAGING_PARAMS,
  rowsPerPageOptions = [10, 25, 50],
}) => {
  const t = useTranslations()
  const classes = useStyles()

  const [deleteFile, setDeleteFile] = useState(null)

  const { page, rowsPerPage, totalRows } = tableParams

  const showLabelDisplayedRows = () => {
    if (!totalRows) return null
    const indexInitRow = (page + 1) * rowsPerPage - rowsPerPage + 1
    const indexLastRow = Math.min(indexInitRow + rowsPerPage - 1, totalRows)
    return `${indexInitRow}-${indexLastRow} de ${totalRows}`
  }

  const handleRequestSort = (event, sortColumn) => {
    const getOppositeSortType = sortType =>
      sortType === 'asc' ? 'desc' : 'asc'

    onChangeTableParams({
      ...tableParams,
      sortColumn,
      sortType:
        sortColumn === tableParams.sortColumn
          ? getOppositeSortType(tableParams.sortType)
          : defaultSortType,
    })
  }

  // eslint-disable-next-line
  const handleChangePage = (event, newPage) => {
    onChangeTableParams({ ...tableParams, page: newPage })
  }

  const handleChangeRowsPerPage = event => {
    onChangeTableParams({
      ...tableParams,
      page: 0,
      rowsPerPage: parseInt(event.target.value, 10),
    })
  }

  return (
    <>
      <ModalLayout
        onClose={() => setDeleteFile(null)}
        isOpen={deleteFile ?? false}
        actions={
          <>
            <Button
              type="button"
              size="small"
              variant="outlined"
              onClick={() => setDeleteFile(null)}
            >
              {t('action:cancel')}
            </Button>
            <Button
              type="submit"
              size="small"
              onClick={() => {
                handleSubmitDelete(deleteFile?.file_id)
                setDeleteFile(null)
              }}
            >
              {t('action:confirm')}
            </Button>
          </>
        }
        content={
          <>
            <Typography variant="body2" className={classes.paragraphText}>
              {t('shipping-recording:delete-file-body1')}
            </Typography>
            <Typography variant="body2">
              {t('shipping-recording:delete-file-body2', {
                shippingTracking: (
                  <Link
                    to={routes.SHIPPINGS_TRACKING.path}
                    style={{ color: 'red' }}
                  >
                    {t('shipping-tracking:title')}
                  </Link>
                ),
              })}
            </Typography>
          </>
        }
        title={t('shipping-recording:delete-file')}
      />
      <TableContainer>
        <MuiTable aria-labelledby="tableTitle" aria-label="enhanced table">
          <EnhancedTableHead
            headCells={SHIPPINGS_RECORDING_TABLE_HEAD_CELLS}
            order={tableParams.sortType}
            orderBy={tableParams.sortColumn}
            onRequestSort={handleRequestSort}
          />
          <TableBody>
            {stableSort(
              rows,
              getComparator(tableParams.sortType, tableParams.sortColumn),
            ).map((row, index) => {
              const id = row.info?.shippingCode

              return (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={index}>
                  <TableRow
                    hover
                    tabIndex={-1}
                    key={id}
                    className={clsx(row?.details?.severity)}
                  >
                    {SHIPPINGS_RECORDING_TABLE_HEADER_ORDER?.map((key, index) =>
                      index === 1 ? (
                        <TableCell key={key} className="main">
                          {row[key]}
                        </TableCell>
                      ) : key === 'status' ? (
                        ['PROCESSING', 'PENDING'].includes(row[key]) ||
                        (row['status'] === 'ERRORED' &&
                          !row?.error_file?.resource_provider) ? (
                          <TableCell key={key}>
                            <Tooltip
                              title={t('tooltip:processing-request')}
                              arrow
                              placement="top"
                            >
                              <Box>
                                <Loader size={20} padding={0} />
                              </Box>
                            </Tooltip>
                          </TableCell>
                        ) : (
                          <TableCell key={key}>
                            <Box width={20} />
                          </TableCell>
                        )
                      ) : key === 'upload_date' ? (
                        <TableCell key={key}>
                          {format(
                            parseISO(row['upload_datetime']),
                            'dd/MM/yyyy',
                          )}
                        </TableCell>
                      ) : key === 'upload_hour' ? (
                        <TableCell key={key}>
                          {format(parseISO(row['upload_datetime']), 'HH:mm')}
                        </TableCell>
                      ) : key === 'success_lines' ? (
                        ['PROCESSING', 'PENDING'].includes(row['status']) ||
                        (row['status'] === 'ERRORED' &&
                          !row?.error_file?.resource_provider) ? (
                          <TableCell key={key}></TableCell>
                        ) : (
                          <TableCell key={key}>
                            <StatusChip
                              quantity={
                                row['total_lines'] - row['error_lines'] ?? '-'
                              }
                            />
                          </TableCell>
                        )
                      ) : key === 'error_lines' ? (
                        ['PROCESSING', 'PENDING'].includes(row['status']) ||
                        (row['status'] === 'ERRORED' &&
                          !row?.error_file?.resource_provider) ? (
                          <TableCell key={key}></TableCell>
                        ) : (
                          <TableCell key={key}>
                            <StatusChip
                              quantity={row[key] ?? 0}
                              hasIncident={true}
                            />
                            {row?.error_file?.resource_provider ? (
                              <Button
                                className={classes.csvButton}
                                variant={'outlined'}
                                onClick={() =>
                                  handleSubmitGetDetail(
                                    row?.error_file?.resource_id,
                                    row?.error_file?.resource_provider,
                                    row?.file_extension,
                                    row?.file_name?.replace(/\.[^.]+$/, ''),
                                  )
                                }
                                startIcon={<Download />}
                              >
                                {'CSV'}
                              </Button>
                            ) : null}
                          </TableCell>
                        )
                      ) : key === 'actionButtons' ? (
                        <TableCell key={key}>
                          <NavLink to={routes.SHIPPINGS_TRACKING.linkTo}>
                            <Tooltip
                              title={t('tooltip:go-tracking')}
                              arrow
                              placement="top"
                            >
                              <Path className={classes.actionButtons} />
                            </Tooltip>
                          </NavLink>
                          <Delete
                            onClick={() => setDeleteFile(row)}
                            className={classes.actionButtons}
                          />
                        </TableCell>
                      ) : (
                        <TableCell key={key}>{row[key]}</TableCell>
                      ),
                    )}
                  </TableRow>
                </React.Fragment>
              )
            })}
          </TableBody>
        </MuiTable>
      </TableContainer>
      {true ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
          py={2}
          px={1}
        >
          <EnhancedTableToolbar numTotal={totalRows} numSelected={0} />
          <TablePagination
            component="div"
            rowsPerPageOptions={rowsPerPageOptions}
            labelRowsPerPage={t('noun:shipments-per-page')}
            labelDisplayedRows={showLabelDisplayedRows}
            count={totalRows ?? 1}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      ) : null}
    </>
  )
}

ShippingsRecordingTable.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      info: PropTypes.oneOfType([
        PropTypes.shape({
          numero_colegiado: PropTypes.string,
          nombre: PropTypes.string,
          apellidos: PropTypes.string,
          modalidades: PropTypes.string,
          farmacia: PropTypes.string,
          telefono: PropTypes.string,
          correo_electronico: PropTypes.string,
        }),
        PropTypes.shape({
          nombre: PropTypes.string,
          telefono: PropTypes.string,
          correo_electronico: PropTypes.string,
          estado: PropTypes.string,
        }),
        PropTypes.shape({
          year: PropTypes.string,
          day: PropTypes.string,
          reason: PropTypes.string,
          type: PropTypes.string,
          locality: PropTypes.string,
        }),
      ]),
    }),
  ),
  tableParams: PropTypes.shape({
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    totalRows: PropTypes.number,
    sortType: PropTypes.string,
    sortColumn: PropTypes.string,
  }),
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.string),
  onChangeTableParams: PropTypes.func,
  defaultSortType: PropTypes.oneOf(['desc', 'asc']),
  handleSubmitDelete: PropTypes.func,
  handleSubmitGetDetail: PropTypes.func,
}

export default ShippingsRecordingTable
